// Vendor Debt Dashboard — admin-only, org-wide aggregate view of money owed
// to vendors across ALL vendors. Wired to ONE combined backend query
// (getVendorDebtDashboardDataAction -> getVendorDebtDashboard) following the
// same pattern as DashboardScreen.tsx / VendorDashboardScreen.tsx.
//
// This is deliberately a NEW, standalone screen — VendorDashboardScreen.tsx
// is strictly Vendor + Vendor Purchase focused "by design" and has no debt
// widgets; that exclusion stands, this is not a tab bolted onto that screen.
//
// No date filter control — always shows the current financial year
// (Apr-to-Mar, see getFinancialYearRange below) with no way to pick a
// different range. Balance figures (Total Pending Debt, Total Paid Amount,
// Vendors with Outstanding Debt, the Vendor Debt Summary table, Top Vendors
// by Outstanding Debt) are always CURRENT/all-time — a running total doesn't
// stop counting just because it fell outside the selected window; every
// other figure (Total Debt, Vendors with Active Debt, the trend, Recent Debt
// Transactions) reflects the financial year. See the backend's
// VendorDebtService.getVendorDebtDashboard for the exact rationale (it still
// accepts optional vendorId/entryType params — this screen just doesn't
// surface controls for them).
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { ColumnDef } from "@tanstack/react-table";
import { Building2, Wallet, TrendingUp, AlertTriangle, HandCoins, UserX } from "lucide-react";
import AppConstant from "../../../Globals/AppConstants";
import { getVendorDebtDashboardDataAction } from "../../../Store/actions/apparelAction";
import ApparelMenu from "../../SubComponents/apparelMenu";
import { StatCard } from "../../../shared/stat-card";
import { DataTable } from "../../../shared/data-table";
import { Badge } from "ui/badge";
import { Skeleton } from "ui/skeleton";
import { ChartCard } from "./ChartCard";
import { TrendAreaChart } from "./TrendAreaChart";
import { CategoryBarChart } from "./CategoryBarChart";

const inr = (v: any) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v) || 0);
const num = (v: any) => new Intl.NumberFormat("en-IN").format(Number(v) || 0);
const PAGE_SIZE = 10;

// Indian financial year: 01-Apr of `startYear` to 31-Mar of `startYear + 1`.
// Before April, "today" still falls in the FY that started the previous
// calendar year.
const getFinancialYearRange = (referenceDate: Date = new Date()) => {
  const startYear = referenceDate.getMonth() >= 3 ? referenceDate.getFullYear() : referenceDate.getFullYear() - 1;
  const endYear = startYear + 1;
  return {
    fromDate: moment({ year: startYear, month: 3, day: 1 }).format("YYYY-MM-DD"),
    toDate: moment({ year: endYear, month: 2, day: 31 }).format("YYYY-MM-DD"),
    label: `FY ${startYear}-${endYear}`,
  };
};

const TABLE_TABS = [
  { key: "summary", label: "Vendor Debt Summary" },
  { key: "recent", label: "Recent Debt Transactions" },
] as const;
type TableTabKey = (typeof TABLE_TABS)[number]["key"];

// The only two real entry types (see file header) mapped to the dashboard's
// requested labels.
const ENTRY_TYPE_LABELS: Record<string, string> = {
  DEBT: "Purchase Debt",
  PAYMENT: "Debt Payment",
};

const VendorDebtDashboardScreen = () => {
  const dispatch = useDispatch();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getVendorDebtDashboardData, getVendorDebtDashboardDataLoad } = apparelReducerState;

  const [page, setPage] = React.useState(1);
  const [recentPage, setRecentPage] = React.useState(1);
  const [activeTab, setActiveTab] = React.useState<TableTabKey>("summary");
  const financialYear = React.useMemo(() => getFinancialYearRange(), []);

  const fetchDashboard = React.useCallback(
    (selectedPage: number, selectedRecentPage: number) => {
      const p = selectedPage || 1;
      const rp = selectedRecentPage || 1;
      dispatch(
        getVendorDebtDashboardDataAction({
          vendorId: "",
          entryType: "",
          granularity: "month",
          fromDate: financialYear.fromDate,
          toDate: financialYear.toDate,
          limit: PAGE_SIZE,
          offset: (p - 1) * PAGE_SIZE,
          recentLimit: PAGE_SIZE,
          recentOffset: (rp - 1) * PAGE_SIZE,
        })
      );
    },
    [dispatch, financialYear]
  );

  React.useEffect(() => {
    fetchDashboard(page, recentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchDashboard(nextPage, recentPage);
  };

  const onRecentPageChange = (nextPage: number) => {
    setRecentPage(nextPage);
    fetchDashboard(page, nextPage);
  };

  const data = getVendorDebtDashboardData?.getVendorDebtDashboard;
  const summary = data?.summary;
  const loading = getVendorDebtDashboardDataLoad && !summary;
  const chartsLoading = getVendorDebtDashboardDataLoad && !data;

  const topVendors = (data?.topVendorsByOutstanding || []).map((v: any) => ({
    label: v.vendorName,
    value: Number(v.outstandingDebt) || 0,
  }));

  const debtIncurredTrend = (data?.trend || []).map((t: any) => ({ date: t.date, value: Number(t.debtIncurred) || 0 }));
  const paidTrend = (data?.trend || []).map((t: any) => ({ date: t.date, value: Number(t.paid) || 0 }));

  const recentEntries = data?.recentEntries?.rows || [];
  const recentEntriesPage = data?.recentEntries?.page;

  const vendorDebtSummaryRows = data?.vendorDebtSummary?.rows || [];
  const vendorDebtSummaryPage = data?.vendorDebtSummary?.page;

  const vendorDebtSummaryColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "vendorName", header: "Vendor", cell: ({ row }) => row.original?.vendorName },
      { id: "totalDebt", header: "Total Debt", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.totalDebt)}</span> },
      { id: "paidAmount", header: "Paid Amount", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.paidAmount)}</span> },
      { id: "pendingDebt", header: "Pending Debt", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.pendingDebt)}</span> },
    ],
    []
  );

  const recentEntriesColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "vendorName", header: "Vendor", cell: ({ row }) => row.original?.vendorName },
      {
        id: "entryType",
        header: "Type",
        cell: ({ row }) => (
          <Badge variant={row.original?.entryType === "PAYMENT" ? "success" : "warning"}>
            {ENTRY_TYPE_LABELS[row.original?.entryType] || row.original?.entryType}
          </Badge>
        ),
      },
      {
        id: "amount",
        header: "Amount",
        cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.amount)}</span>,
      },
      {
        id: "transactionDate",
        header: "Date",
        cell: ({ row }) => (row.original?.transactionDate ? moment(row.original.transactionDate).format("DD-MMM-YYYY") : "-"),
      },
      { id: "notes", header: "Notes", cell: ({ row }) => row.original?.notes || "-" },
    ],
    []
  );

  const recentEntriesCsvRows = React.useMemo(
    () => recentEntries.map((r: any) => ({
      ...r,
      entryType_label: ENTRY_TYPE_LABELS[r.entryType] || r.entryType,
      transactionDate_fmt: r.transactionDate ? moment(r.transactionDate).format("DD-MMM-YYYY") : "-",
      amount: Math.round(Number(r.amount) || 0),
    })),
    [recentEntries]
  );

  const activeTableProps: Record<TableTabKey, { columns: ColumnDef<any, any>[]; data: any[]; pagination?: any }> = {
    summary: {
      columns: vendorDebtSummaryColumns,
      data: vendorDebtSummaryRows,
      pagination: {
        page: vendorDebtSummaryPage?.currentPage || page,
        total: vendorDebtSummaryPage?.totalCount || 0,
        pageSize: PAGE_SIZE,
        onChange: onPageChange,
      },
    },
    recent: {
      columns: recentEntriesColumns,
      data: recentEntries,
      pagination: {
        page: recentEntriesPage?.currentPage || recentPage,
        total: recentEntriesPage?.totalCount || 0,
        pageSize: PAGE_SIZE,
        onChange: onRecentPageChange,
      },
    },
  };

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <ApparelMenu selectedKey="4" />

      <div className="mb-4 flex items-center gap-1 border-b">
        <button type="button" className="border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground">
          {AppConstant.menus.vendorDebtDashboard}
        </button>
      </div>
      {/* <p className="mb-4 text-sm text-muted-foreground">
        Showing {financialYear.label} ({moment(financialYear.fromDate).format("DD-MMM-YYYY")} – {moment(financialYear.toDate).format("DD-MMM-YYYY")}).
        <span className="ml-2">As of {moment(data?.asOfDate || new Date()).format("DD-MMM-YYYY")}</span>
      </p> */}

      {/* KPI cards — 3 up top, 2 up bottom, both rows full-width (see
          lg:col-span-2 on a 6-col track for the top row, lg:col-span-3 for
          the bottom row: 2+2+2 and 3+3 each sum to 6). Currency values stay
          on one line (nowrap + ellipsis) since the cards are wide enough at
          this split that real amounts never need to truncate. */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
        {loading ? (
          <>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl lg:col-span-2" />
            ))}
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i + 3} className="h-24 w-full rounded-xl lg:col-span-3" />
            ))}
          </>
        ) : (
          <>
            <StatCard
              className="lg:col-span-2"
              label="Total Debt"
              value={inr(summary?.totalDebt)}
              icon={TrendingUp}
              valueClassName="!whitespace-nowrap overflow-hidden text-ellipsis"
            />
            <StatCard
              className="lg:col-span-2"
              label="Total Pending Debt"
              value={inr(summary?.totalPendingDebt)}
              icon={AlertTriangle}
              valueClassName="!whitespace-nowrap overflow-hidden text-ellipsis"
            />
            <StatCard
              className="lg:col-span-2"
              label="Total Paid Amount"
              value={inr(summary?.totalPaidAmount)}
              icon={HandCoins}
              valueClassName="!whitespace-nowrap overflow-hidden text-ellipsis"
            />
            <StatCard
              className="lg:col-span-3"
              label="Vendors with Outstanding Debt"
              value={num(summary?.vendorsWithOutstandingDebt)}
              icon={UserX}
              valueClassName="!whitespace-nowrap overflow-hidden text-ellipsis"
            />
            <StatCard
              className="lg:col-span-3"
              label="Vendors with Active Debt"
              value={num(summary?.vendorsWithActiveDebt)}
              icon={Building2}
              valueClassName="!whitespace-nowrap overflow-hidden text-ellipsis"
            />
          </>
        )}
      </div>

      {/* Charts */}
      <h3 className="mb-2 text-sm font-semibold text-foreground">Debt vs Payment Trend</h3>
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Debt Incurred" loading={chartsLoading} isEmpty={!debtIncurredTrend.length}>
          <TrendAreaChart data={debtIncurredTrend} valueFormatter={inr} />
        </ChartCard>
        <ChartCard title="Paid Amount" loading={chartsLoading} isEmpty={!paidTrend.length}>
          <TrendAreaChart data={paidTrend} valueFormatter={inr} />
        </ChartCard>
        <ChartCard title="Top Vendors by Outstanding Debt" loading={chartsLoading} isEmpty={!topVendors.length} className="lg:col-span-2">
          <CategoryBarChart data={topVendors} valueFormatter={inr} layout="vertical" />
        </ChartCard>
      </div>

      {/* Vendor Debt Summary / Recent Debt Transactions — tabbed, same
          pattern as CustomerCreditDashboardScreen.tsx's table tabs. */}
      <div className="mb-2 flex flex-wrap gap-1 border-b">
        {TABLE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={
              activeTab === tab.key
                ? "border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground"
                : "border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            }
          >
            {tab.label}
          </button>
        ))}
      </div>
      <DataTable
        columns={activeTableProps[activeTab].columns}
        data={activeTableProps[activeTab].data}
        loading={chartsLoading}
        csvExport={
          activeTab === "summary"
            ? { fileName: `vendor-debt-summary_${moment().format("YYYY-MM-DD")}`, rows: vendorDebtSummaryRows, columns: [{ key: "vendorName", header: "Vendor" }, { key: "totalDebt", header: "Total Debt" }, { key: "paidAmount", header: "Paid Amount" }, { key: "pendingDebt", header: "Pending Debt" }] }
            : { fileName: `recent-debt-transactions_${moment().format("YYYY-MM-DD")}`, rows: recentEntriesCsvRows, columns: [{ key: "vendorName", header: "Vendor" }, { key: "entryType_label", header: "Type" }, { key: "amount", header: "Amount" }, { key: "transactionDate_fmt", header: "Date" }, { key: "notes", header: "Notes" }] }
        }
        pagination={activeTableProps[activeTab].pagination}
      />
    </div>
  );
};

export default VendorDebtDashboardScreen;
